/**
 * Diabolical Tees announcement bar.
 * <dt-announcement-rotator>: shows one message at a time and steps forward, then back
 * (0 > 1 > 2 > 1 > 0), sliding 24px with a fade. data-interval is in seconds.
 * data-offset="half" delays the first step by half an interval on desktop so two rotators alternate.
 * data-desktop-only stops rotation below 750px (the element is hidden there by CSS).
 * <dt-announcement-countdown>: counts down to data-deadline (ISO 8601 with offset). With
 * data-repeat-days it restarts every N days. Hides itself and toggles .has-countdown on the bar.
 */

const MOBILE_QUERY = '(max-width: 749px)';
const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';

class DtAnnouncementRotator extends HTMLElement {
  connectedCallback() {
    this.mobile = window.matchMedia(MOBILE_QUERY);
    this.reduced = window.matchMedia(REDUCED_QUERY);
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.mobile.addEventListener('change', () => this.setup(), { signal });
    this.reduced.addEventListener('change', () => this.setup(), { signal });

    // Theme editor: show the selected message, resume on deselect.
    document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });
    document.addEventListener('shopify:block:deselect', (event) => this.onBlockDeselect(event), { signal });

    this.setup();
  }

  disconnectedCallback() {
    this.stop();
    this.abort?.abort();
  }

  setup() {
    this.stop();
    const isMobile = this.mobile.matches;

    this.messages = [...this.querySelectorAll('[data-dt-message]')];
    this.items = this.messages.filter(
      (item) => item.textContent.trim() && (isMobile || !item.hasAttribute('data-mobile-only'))
    );
    this.index = 0;
    this.direction = 1;
    this.showOnly(this.items[0]);

    const desktopOnly = this.hasAttribute('data-desktop-only');
    if (this.reduced.matches || this.items.length < 2 || (desktopOnly && isMobile)) return;

    this.interval = Math.max(3, Number(this.dataset.interval) || 6) * 1000;
    const firstDelay = !isMobile && this.dataset.offset === 'half' ? this.interval / 2 : this.interval;

    this.timeout = window.setTimeout(() => {
      this.advance();
      this.timer = window.setInterval(() => this.advance(), this.interval);
    }, firstDelay);
  }

  showOnly(target) {
    for (const item of this.messages) {
      item.hidden = item !== target;
      item.removeAttribute('aria-hidden');
    }
  }

  advance() {
    if (document.hidden || this.contains(document.activeElement)) return;

    if (this.index === this.items.length - 1) this.direction = -1;
    else if (this.index === 0) this.direction = 1;

    const previous = this.items[this.index];
    this.index += this.direction;
    const next = this.items[this.index];
    if (!previous || !next) return;

    next.hidden = false;
    previous.setAttribute('aria-hidden', 'true');

    const distance = this.direction * 24;
    const timing = { duration: 320, easing: 'cubic-bezier(0.4, 0, 0.2, 1)' };
    this.animations = [
      previous.animate(
        [
          { transform: 'translateX(0)', opacity: 1 },
          { transform: `translateX(${distance}px)`, opacity: 0 },
        ],
        timing
      ),
      next.animate(
        [
          { transform: `translateX(${-distance}px)`, opacity: 0 },
          { transform: 'translateX(0)', opacity: 1 },
        ],
        timing
      ),
    ];

    Promise.all(this.animations.map((animation) => animation.finished))
      .then(() => {
        previous.hidden = true;
        previous.removeAttribute('aria-hidden');
        this.animations = [];
      })
      .catch(() => {});
  }

  stop() {
    window.clearTimeout(this.timeout);
    window.clearInterval(this.timer);
    for (const animation of this.animations ?? []) animation.cancel();
    this.animations = [];
  }

  /** @param {Event} event */
  onBlockSelect(event) {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    const message = event.target.closest('[data-dt-message]');
    if (!message) return;
    this.stop();
    this.showOnly(message);
  }

  /** @param {Event} event */
  onBlockDeselect(event) {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;
    this.setup();
  }
}

class DtAnnouncementCountdown extends HTMLElement {
  connectedCallback() {
    this.bar = this.closest('.dt-announcement');
    this.digits = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };
    this.timerElement = this.querySelector('[role="timer"]');
    this.tick();
    this.timer = window.setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.timer);
  }

  /** @returns {number | null} end time in ms, or null when the deadline is invalid */
  getEnd(now) {
    const firstEnd = Date.parse(String(this.dataset.deadline || '').trim().replace(' ', 'T'));
    if (!Number.isFinite(firstEnd)) return null;

    const period = Number(this.dataset.repeatDays || 0) * 86400000;
    if (period > 0 && now >= firstEnd) {
      return firstEnd + (Math.floor((now - firstEnd) / period) + 1) * period;
    }
    return firstEnd;
  }

  tick() {
    const now = Date.now();
    const end = this.getEnd(now);
    const visible = end !== null && end > now;

    this.hidden = !visible;
    this.bar?.classList.toggle('has-countdown', visible);
    if (!visible) return;

    const total = Math.ceil((end - now) / 1000);
    const parts = {
      days: Math.floor(total / 86400),
      hours: Math.floor((total % 86400) / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60,
    };

    for (const [unit, element] of Object.entries(this.digits)) {
      if (element) element.textContent = String(parts[unit]).padStart(2, '0');
    }
    this.timerElement?.setAttribute(
      'aria-label',
      `${parts.days} days, ${parts.hours} hours, ${parts.minutes} minutes, ${parts.seconds} seconds remaining`
    );
  }
}

if (!customElements.get('dt-announcement-rotator')) {
  customElements.define('dt-announcement-rotator', DtAnnouncementRotator);
}
if (!customElements.get('dt-announcement-countdown')) {
  customElements.define('dt-announcement-countdown', DtAnnouncementCountdown);
}
